"use client";

import type React from "react";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  useToast,
  HStack,
  Icon,
  CloseButton,
  VStack,
  Box,
  Flex,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import { callService } from "../../services/callService.ts";
import {
  agentService,
  type RetellLLM,
} from "../../services/agentService.ts";
import { useAppTheme } from "../../ThemeContext.tsx";

interface MakeOutboundCallDialogProps {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber: string;
  agentId: string | null;
}

function collectVoiceTemplateText(llm: RetellLLM | null): string {
  if (!llm) return "";
  const parts: string[] = [];
  if (llm.general_prompt) parts.push(llm.general_prompt);
  if (llm.begin_message) parts.push(llm.begin_message);
  if (Array.isArray(llm.states) && llm.states.length > 0) {
    try {
      parts.push(JSON.stringify(llm.states));
    } catch {
      parts.push(String(llm.states));
    }
  }
  return parts.join("\n");
}

export const MakeOutboundCallDialog: React.FC<MakeOutboundCallDialogProps> = ({
  isOpen,
  onClose,
  phoneNumber,
  agentId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [toNumber, setToNumber] = useState("");
  const [templateSourceText, setTemplateSourceText] = useState("");
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [dynamicVariables, setDynamicVariables] = useState<
    Record<string, string>
  >({});
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const modalContentBg = isDark ? "gray.800" : "white";
  const labelColor = isDark ? "gray.300" : "gray.700";
  const infoTextColor = isDark ? "gray.400" : "gray.600";
  const panelBg = isDark ? "gray.700" : "gray.50";
  const panelBorder = isDark ? "gray.600" : "gray.200";

  useLayoutEffect(() => {
    if (isOpen && agentId) {
      setLoadingTemplate(true);
    }
  }, [isOpen, agentId]);

  useEffect(() => {
    if (!isOpen || !agentId) {
      setTemplateSourceText("");
      setDynamicVariables({});
      setLoadingTemplate(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const agent = await agentService.get(agentId);
        if (cancelled) return;

        if (
          agent.response_engine?.type === "retell-llm" &&
          agent.response_engine.llm_id
        ) {
          const llm = await agentService.getLLMData(
            agent.response_engine.llm_id
          );
          if (cancelled) return;
          setTemplateSourceText(collectVoiceTemplateText(llm));
        } else {
          setTemplateSourceText("");
        }
      } catch (e) {
        console.error("Failed to load voice agent template for dynamic vars:", e);
        if (!cancelled) setTemplateSourceText("");
      } finally {
        if (!cancelled) setLoadingTemplate(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, agentId]);

  const extractedVariables = useMemo(() => {
    const regex = /{{\s*([^{}]+?)\s*}}/g;
    const variables = new Set<string>();
    let match: RegExpExecArray | null = regex.exec(templateSourceText);
    while (match) {
      const name = (match[1] || "").trim();
      if (name) variables.add(name);
      match = regex.exec(templateSourceText);
    }
    return Array.from(variables);
  }, [templateSourceText]);

  const handleSubmit = async () => {
    if (!toNumber) {
      toast({
        title: "Phone number is required",
        status: "error",
        duration: 3000,
      });
      return;
    }

    if (!agentId) {
      toast({
        title: "No outbound agent configured",
        description:
          "Please configure an outbound agent for this phone number first",
        status: "error",
        duration: 5000,
      });
      return;
    }

    setIsLoading(true);

    try {
      await callService.createPhoneCall(agentId, phoneNumber, toNumber, {
        dynamic_vars_values: dynamicVariables,
      });

      toast({
        title: "Outbound call initiated",
        description: `Calling ${toNumber} from ${phoneNumber}`,
        status: "success",
        duration: 3000,
      });
      setToNumber("");
      setDynamicVariables({});
      onClose();
    } catch (error: any) {
      toast({
        title: "Error making outbound call",
        description:
          error.response?.data?.message ||
          "An error occurred while making the call",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg={modalContentBg} maxW="500px">
        <ModalHeader
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pb={4}
          color={labelColor}
        >
          Make Outbound Call
          <CloseButton onClick={onClose} />
        </ModalHeader>

        <ModalBody>
          <FormControl mb={4}>
            <FormLabel color={labelColor}>Phone Number</FormLabel>
            <Input
              value={toNumber}
              onChange={(e) => setToNumber(e.target.value)}
              placeholder="e.g. +11234567890"
              borderColor={isDark ? "gray.600" : "gray.300"}
              bg={isDark ? "gray.700" : "white"}
              color={isDark ? "white" : "gray.800"}
            />
          </FormControl>

          {agentId && loadingTemplate && (
            <Box
              bg={panelBg}
              border="1px solid"
              borderColor={panelBorder}
              borderRadius="md"
              p={6}
              mb={4}
              minH="120px"
            >
              <Center flexDirection="column" gap={3}>
                <Spinner
                  thickness="3px"
                  speed="0.65s"
                  emptyColor={isDark ? "gray.600" : "gray.200"}
                  color={isDark ? "gray.300" : "gray.500"}
                  size="md"
                />
                <Text fontSize="sm" color={infoTextColor} textAlign="center">
                  Loading dynamic variables from your agent…
                </Text>
              </Center>
            </Box>
          )}

          {!loadingTemplate && extractedVariables.length > 0 && (
            <Box
              bg={panelBg}
              border="1px solid"
              borderColor={panelBorder}
              borderRadius="md"
              p={3}
              mb={4}
            >
              <Text fontWeight="semibold" fontSize="sm" color={labelColor}>
                Dynamic Variables
              </Text>
              <Text fontSize="xs" color={infoTextColor} mt={1} mb={3}>
                From your agent prompt, begin message, and flow states (Retell
                LLM)
              </Text>

              <Flex mb={2} fontWeight="medium" fontSize="xs" color={labelColor}>
                <Box flex="1">Variable Name</Box>
                <Box flex="1">Test Value</Box>
              </Flex>

              <VStack spacing={2} align="stretch">
                {extractedVariables.map((variable) => (
                  <HStack key={variable} spacing={2}>
                    <Input
                      size="sm"
                      value={variable}
                      isReadOnly
                      fontSize="xs"
                      bg={isDark ? "gray.800" : "white"}
                      borderColor={isDark ? "gray.600" : "gray.300"}
                    />
                    <Input
                      size="sm"
                      placeholder="Enter value"
                      value={dynamicVariables[variable] || ""}
                      fontSize="xs"
                      bg={isDark ? "gray.800" : "white"}
                      borderColor={isDark ? "gray.600" : "gray.300"}
                      onChange={(e) =>
                        setDynamicVariables((prev) => ({
                          ...prev,
                          [variable]: e.target.value,
                        }))
                      }
                    />
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}

          <VStack
            spacing={2}
            color={infoTextColor}
            fontSize="sm"
            align="start"
          >
            <HStack spacing={2} color={infoTextColor}>
              <Icon as={InfoIcon} />
              <Text>International calls are currently not supported.</Text>
            </HStack>
          </VStack>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={!agentId}
          >
            Call
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
